import { isFunction } from "@suse-o11y/is-function";
import type { History } from "history";
import {
	type AnyAction,
	type Middleware,
	type PreloadedState,
	type Store,
	type StoreEnhancer,
	applyMiddleware,
	compose,
	createStore,
} from "redux";
import { createEpicMiddleware } from "redux-observable";

import type { QueryClient } from "../../queries/react-query";

import { RootEpic } from "./RootEpic";
import { RootReducer } from "./RootReducer";
import { type RootState, initialRootState } from "./RootState";

// biome-ignore lint/style/noRestrictedGlobals: redux devtools entry point
const { __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: extensionCompose } = window as {
	__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: (input: unknown) => typeof compose;
};

const composeEnhancers = import.meta.env.DEV && isFunction(extensionCompose) ? extensionCompose({}) : compose;

export interface EpicDependencies {
	queryClient: QueryClient;
	history: History;
}

export interface CreateReduxStoreOptions extends EpicDependencies {
	initialState?: RootState;
}

export const createReduxStore = ({
	initialState = initialRootState,
	...dependencies
}: CreateReduxStoreOptions): Store<RootState> => {
	const epicMiddleware = createEpicMiddleware<AnyAction, AnyAction, RootState, EpicDependencies>({ dependencies });
	const middlewares: Array<Middleware> = [epicMiddleware];
	const enhancer: StoreEnhancer = composeEnhancers(applyMiddleware(...middlewares));
	const store = createStore(RootReducer, initialState as PreloadedState<RootState>, enhancer) as Store<RootState>;

	epicMiddleware.run(RootEpic);
	return store;
};
